import { useState } from "react";
import { Upload } from "antd";
import { UploadCloud, Download, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";
import * as XLSX from "xlsx";

import ToastMsg from "../../../components/common/ToastMsg";
import { Button } from "../../../components/ui/button";
import { WizardFooter, PRIMARY_BTN } from "./WizardFooter";
import { getCSVData } from "../../../components/common/CommonFunctions";
import { EMPLOYEE_MIGRATION_TEMPLATE } from "../../../constants/api";
import {
    FIELDS as DEFAULT_FIELDS,
    REQUIRED_HEADER as DEFAULT_REQUIRED_HEADER,
    headerToKey as defaultHeaderToKey,
    toIsoDate,
    rowHasErrors as defaultRowHasErrors,
    ensureKeys as defaultEnsureKeys,
} from "../migrationConfig";

const MAX_ROWS_DEFAULT = 100;

const normalizeHeaderCell = (h) => String(h || "").replace(/\*/g, "").replace(/\s+/g, "");

/** Locate the header row even when instruction rows contain commas (unquoted CSV). */
const findHeaderRowIndex = (matrix, requiredHeader) => {
    const target = normalizeHeaderCell(requiredHeader);
    for (let i = 0; i < matrix.length; i++) {
        const row = matrix[i];
        if (!row?.length) continue;
        if (normalizeHeaderCell(row[0]) === target) return i;
    }
    return -1;
};

const UploadStep = ({
    options,
    onParsed,
    onCancel,
    fields = DEFAULT_FIELDS,
    requiredHeader = DEFAULT_REQUIRED_HEADER,
    headerToKey = defaultHeaderToKey,
    rowHasErrors = defaultRowHasErrors,
    ensureKeys = defaultEnsureKeys,
    templateUrl,
    templateQuery = {},
    templateFileType = "csv",
    acceptFileTypes = ".csv",
    maxRows = MAX_ROWS_DEFAULT,
    stepTitle = "Step 1 — Upload employee data",
    stepDescription,
}) => {
    const [parsing, setParsing] = useState(false);
    const [fileName, setFileName] = useState("");
    const [rows, setRows] = useState([]);

    const isXlsxTemplate = templateFileType === "xlsx";
    const acceptedExtensions = acceptFileTypes.split(",").map((ext) => ext.trim().toLowerCase()).filter(Boolean);
    const uploadHint = acceptedExtensions.length > 1
        ? `${acceptedExtensions.join(" or ")} from the template`
        : "Only .csv exported from the template";
    const defaultStepDescription = isXlsxTemplate
        ? `Download the Excel template, fill in up to ${maxRows} rows, then upload the CSV or Excel file.`
        : `Download the template, fill in up to ${maxRows} rows, then upload the CSV.`;

    const DATE_KEYS = new Set(fields.filter((f) => f.kind === "date").map((f) => f.key));

    const downloadTemplate = async () => {
        try {
            await getCSVData(templateUrl || EMPLOYEE_MIGRATION_TEMPLATE, templateQuery, templateFileType);
        } catch (e) {
            ToastMsg("error", e.message || "Could not download the template");
        }
    };

    const parseSheet = (file) => {
        setParsing(true);
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const wb = XLSX.read(event.target.result, { type: "array" });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });

                const headerRowIndex = findHeaderRowIndex(matrix, requiredHeader);
                if (headerRowIndex < 0) {
                    ToastMsg("error", "This file doesn't match the downloaded template. Please use the template.");
                    setRows([]);
                    setFileName("");
                    return;
                }

                const headers = matrix[headerRowIndex] || [];
                const keys = headers.map(headerToKey);
                const body = matrix.slice(headerRowIndex + 1);
                const parsed = [];
                for (const raw of body) {
                    if (!raw || raw.every((c) => c === null || c === undefined || c === "")) continue;
                    const row = {};
                    keys.forEach((key, j) => {
                        if (!key) return;
                        let val = raw[j];
                        if (DATE_KEYS.has(key)) {
                            val = typeof val === "number"
                                ? XLSX.SSF.format("YYYY-MM-DD", val)
                                : toIsoDate(val);
                        } else if (typeof val === "string") {
                            val = val.trim() || null;
                        }
                        row[key] = val ?? null;
                    });
                    parsed.push(row);
                }

                if (parsed.length === 0) {
                    ToastMsg("error", "No data rows found in the sheet. Add at least one record below the header row.");
                    setRows([]);
                    setFileName("");
                    return;
                }
                if (parsed.length > maxRows) {
                    ToastMsg("error", `A migration can hold at most ${maxRows} records.`);
                    setRows([]);
                    setFileName("");
                    return;
                }
                setRows(parsed);
            } catch (e) {
                ToastMsg("error", "Could not read the spreadsheet.");
                setRows([]);
                setFileName("");
            } finally {
                setParsing(false);
            }
        };
        reader.onerror = () => {
            ToastMsg("error", "Could not read the file.");
            setParsing(false);
        };
        reader.readAsArrayBuffer(file);
    };

    const beforeUpload = (file) => {
        const lowerName = file.name.toLowerCase();
        const isAccepted = acceptedExtensions.some((ext) => lowerName.endsWith(ext));
        if (!isAccepted) {
            ToastMsg("error", `Please upload a ${acceptedExtensions.join(" or ")} file.`);
            return Upload.LIST_IGNORE;
        }
        setFileName(file.name);
        parseSheet(file);
        return false; // we handle parsing ourselves; never auto-upload
    };

    const validCount = rows.filter((r) => !rowHasErrors(r, options)).length;
    const conflictCount = rows.length - validCount;

    return (
        <div>
            <div className="p-4 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        {stepTitle}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        {stepDescription || defaultStepDescription}
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                    <Download className="w-4 h-4 mr-1" /> Download Template
                </Button>
            </div>

            <Upload.Dragger
                accept={acceptFileTypes}
                maxCount={1}
                multiple={false}
                showUploadList={false}
                beforeUpload={beforeUpload}
                disabled={parsing}
            >
                <p className="flex justify-center text-sidebar-primary">
                    <UploadCloud className="w-8 h-8" />
                </p>
                <p className="text-sm font-medium mt-2">
                    {parsing
                        ? "Reading sheet…"
                        : isXlsxTemplate
                            ? "Click or drag the filled CSV or Excel file here"
                            : "Click or drag the filled CSV here"}
                </p>
                <p className="text-xs text-muted-foreground">{uploadHint}</p>
            </Upload.Dragger>

            {fileName && rows.length > 0 && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap items-center gap-4">
                    <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <FileSpreadsheet className="w-4 h-4 text-sidebar-primary" /> {fileName}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                        {rows.length} rows detected
                    </span>
                    <span className="flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle2 className="w-4 h-4" /> {validCount} ready
                    </span>
                    {conflictCount > 0 && (
                        <span className="flex items-center gap-1 text-sm text-amber-600">
                            <AlertTriangle className="w-4 h-4" /> {conflictCount} need attention
                        </span>
                    )}
                </div>
            )}

            </div>

            <WizardFooter>
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button className={PRIMARY_BTN} disabled={rows.length === 0 || parsing} onClick={() => onParsed(ensureKeys(rows))}>
                    Next: Review &amp; Fix
                </Button>
            </WizardFooter>
        </div>
    );
};

export default UploadStep;
